/**
 * CssVariablesStore - state for Variables feature
 */

import {
  type SwatchType,
  type CssVariablesStateType,
  SYNC_STATUS,
} from '@packages/systems/core/utils/StyleTypes';

import {
  type LegacySwatchType,
  cleanSwatches,
} from '@packages/systems/style-legacy/swatches';
import {assoc, emptyArray, omit} from '@packages/utilities/fp/utils';
import {weakMemo} from '@packages/utilities/memo';
import {reportBug} from '@packages/utilities/reporter';
import {incrementString, increment} from '@packages/utilities/string-utils';
import {
  SWATCHES_INJECTED,
  SWATCH_ADDED,
  SWATCH_EDITING_CANCELLED,
  SWATCH_REMOVED,
  SWATCH_EDITED,
  SWATCH_UPDATED,
  SWATCH_UPDATE_BEGAN,
  UNDO,
  REDO,
  ELEMENT_ADDED,
  STYLE_VARIABLE_CREATED,
  STYLE_VARIABLE_UPDATED,
  STYLE_VARIABLES_EPHEMERALLY_UPDATED,
  STYLE_VARIABLE_DELETED,
  STYLE_VARIABLES_LOADED,
  STYLE_VARIABLES_BULK_UPDATED,
  STYLE_VARIABLES_BULK_SAVE_ALL,
  STYLE_VARIABLES_BULK_SAVE_ALL_SUCCESS,
  STYLE_VARIABLES_GROUP_UPDATED,
  STYLE_VARIABLES_GROUP_DUPLICATED,
  STYLE_VARIABLES_REORDERED,
  STYLE_VARIABLE_GROUP_REORDERED,
  STYLE_VARIABLE_EPHEMERAL_UPDATE_CANCELLED,
  STYLE_VARIABLE_MODE_CREATED,
  STYLE_VARIABLE_MODE_CONFIG_UPDATED,
  STYLE_VARIABLE_MODE_VALUE_UPDATED,
  STYLE_VARIABLE_MODE_DELETED,
  STYLE_VARIABLE_MODE_REORDERED,
  STYLE_VARIABLE_COLLECTION_CREATED,
  STYLE_VARIABLE_COLLECTION_UPDATED,
  STYLE_VARIABLE_COLLECTION_DELETED,
  STYLE_VARIABLE_COLLECTION_REORDERED,
  STYLE_VARIABLES_CROSS_COLLECTION_MOVED,

  // Failed actions
  STYLE_VARIABLE_CREATED_FAILED,
  STYLE_VARIABLE_UPDATED_FAILED,
  STYLE_VARIABLE_DELETED_FAILED,
  STYLE_VARIABLE_BULK_UPDATE_FAILED,
  STYLE_VARIABLES_BULK_SAVE_ALL_FAILED,
  STYLE_VARIABLE_MODE_DELETED_FAILED,
  STYLE_VARIABLE_COLLECTION_CREATED_FAILED,
  STYLE_VARIABLE_COLLECTION_UPDATED_FAILED,
  STYLE_VARIABLE_COLLECTION_DELETED_FAILED,
  STYLE_VARIABLE_MODE_RESTORED,
} from '@packages/systems/designer/actions';

import type {
  DesignerAction,
  DesignerActionFromCreator,
} from '@packages/systems/designer/flux';
import {getPluginsFromState} from '@packages/utilities/platform-selectors';
import {Plugins, ElementPreset} from '@packages/systems/wfdl';
import {elementAdded} from '@packages/systems/element-action-creators';
import {
  getPluginName,
  getName,
  qualify,
  type PluginName,
  Qualified,
} from '@packages/systems/wfdl-Qualified';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';
import {
  styleVariableCreated,
  styleVariableUpdated,
  styleVariablesEphemerallyUpdated,
  styleVariableDeleted,
  styleVariablesLoaded,
  styleVariablesBulkUpdated,
  styleVariablesGroupUpdated,
  styleVariablesGroupDuplicated,
  styleVariablesReordered,
  styleVariableGroupReordered,
  styleVariableEphemeralUpdateCancelled,
  styleVariableCollectionCreated,
  styleVariableCollectionUpdated,
  styleVariableCollectionDeleted,
  styleVariableCollectionReordered,
  styleVariableModeCreated,
  styleVariableModeDeleted,
  styleVariableModeConfigUpdated,
  styleVariableModeReordered,
  styleVariableModeValueUpdated,
  styleVariablesCrossCollectionMoved,
  styleVariableModeRestored,
} from '../actions/CssVariablesActionCreators';
import {getLibrariesVariablesOriginIdMap} from '@packages/systems/libraries';
import {
  handleStyleVariableGroupReorderedAction,
  handleStyleVariablesReorderedAction,
} from './handleStyleVariablesReorderedAction';
import {handleStyleVariablesBulkUpdatedAction} from './handleStyleVariablesBulkUpdatedAction';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {applyBrand} from '@packages/utilities/type-safety';
import {isVariableReference} from '@packages/systems/core/utils/StyleVariables';
import {
  styleVariablesOrionAppVariablesCreated,
  STYLE_VARIABLES_ORION_APP_VARIABLES_CREATED,
} from '@packages/systems/ai/orion/action-creators';

type State = CssVariablesStateType;

type ActionWithSwatchesParams = {
  swatches: SwatchType[];
};

type Action =
  | DesignerAction<typeof UNDO, void>
  | DesignerAction<typeof REDO, void>
  | DesignerAction<typeof SWATCH_UPDATE_BEGAN, void>
  | DesignerAction<typeof SWATCH_ADDED, ActionWithSwatchesParams>
  | DesignerAction<typeof SWATCH_UPDATED, ActionWithSwatchesParams>
  | DesignerAction<typeof SWATCH_REMOVED, ActionWithSwatchesParams>
  | DesignerAction<typeof SWATCH_EDITING_CANCELLED, ActionWithSwatchesParams>
  | DesignerAction<typeof SWATCH_EDITED, ActionWithSwatchesParams>
  | DesignerAction<typeof SWATCHES_INJECTED, {payload: LegacySwatchType[]}>
  | DesignerAction<typeof STYLE_VARIABLE_CREATED_FAILED, void>
  | DesignerAction<typeof STYLE_VARIABLE_UPDATED_FAILED, void>
  | DesignerAction<typeof STYLE_VARIABLE_DELETED_FAILED, void>
  | DesignerAction<typeof STYLE_VARIABLE_BULK_UPDATE_FAILED, void>
  | DesignerAction<typeof STYLE_VARIABLE_BULK_UPDATE_FAILED, void>
  | DesignerAction<typeof STYLE_VARIABLES_BULK_SAVE_ALL_FAILED, void>
  | DesignerAction<typeof STYLE_VARIABLES_BULK_SAVE_ALL_SUCCESS, void>
  | DesignerAction<typeof STYLE_VARIABLES_BULK_SAVE_ALL, void>
  | DesignerAction<typeof STYLE_VARIABLE_MODE_DELETED_FAILED, void>
  | DesignerAction<typeof STYLE_VARIABLE_COLLECTION_CREATED_FAILED, void>
  | DesignerAction<typeof STYLE_VARIABLE_COLLECTION_UPDATED_FAILED, void>
  | DesignerAction<typeof STYLE_VARIABLE_COLLECTION_DELETED_FAILED, void>
  | DesignerActionFromCreator<typeof styleVariablesOrionAppVariablesCreated>
  | DesignerActionFromCreator<typeof elementAdded>
  | DesignerActionFromCreator<typeof styleVariableCreated>
  | DesignerActionFromCreator<typeof styleVariableUpdated>
  | DesignerActionFromCreator<typeof styleVariablesBulkUpdated>
  | DesignerActionFromCreator<typeof styleVariablesGroupUpdated>
  | DesignerActionFromCreator<typeof styleVariablesGroupDuplicated>
  | DesignerActionFromCreator<typeof styleVariablesEphemerallyUpdated>
  | DesignerActionFromCreator<typeof styleVariableEphemeralUpdateCancelled>
  | DesignerActionFromCreator<typeof styleVariableDeleted>
  | DesignerActionFromCreator<typeof styleVariablesLoaded>
  | DesignerActionFromCreator<typeof styleVariablesReordered>
  | DesignerActionFromCreator<typeof styleVariableGroupReordered>
  | DesignerActionFromCreator<typeof styleVariableCollectionCreated>
  | DesignerActionFromCreator<typeof styleVariableCollectionUpdated>
  | DesignerActionFromCreator<typeof styleVariableCollectionDeleted>
  | DesignerActionFromCreator<typeof styleVariableCollectionReordered>
  | DesignerActionFromCreator<typeof styleVariablesCrossCollectionMoved>
  | DesignerActionFromCreator<typeof styleVariableModeCreated>
  | DesignerActionFromCreator<typeof styleVariableModeDeleted>
  | DesignerActionFromCreator<typeof styleVariableModeConfigUpdated>
  | DesignerActionFromCreator<typeof styleVariableModeReordered>
  | DesignerActionFromCreator<typeof styleVariableModeValueUpdated>
  | DesignerActionFromCreator<typeof styleVariableModeRestored>;

const swatchIndex = weakMemo(
  (swatches: State['swatches']): Map<string, SwatchType> => {
    return swatches.reduce((memo, swatch) => {
      memo.set(swatch.id, swatch);
      return memo;
    }, new Map());
  }
);

export const addElementPresetVariable = (
  variable: StyleVariables.AnyVariableType,
  libraryName: string,
  variables: StyleVariables.VariableMap
) => {
  // prefix imported library variables names with library name to
  // group libraries variables separate from user variables

  // @ts-expect-error - type-union woes
  const updatedVariable: StyleVariables.AnyVariableType = {
    ...variable,
    // NOTE: It's actually possible for legacy element presets to contain
    // variables that don't have any `modes` data set, so we explicitly ensure
    // there's always _something_ set here:
    modes: variable.modes ?? {},
    name: `${libraryName}/${variable.name}`,
  };

  const variableWithoutRepeatedName =
    StyleVariables.createVariableWithoutRepeatedName(
      variables,
      updatedVariable
    );

  return StyleVariables.upsertVariable(variables, variableWithoutRepeatedName);
};

/**
 * Adds a library variable collection to the project so that imported library
 * variables that reference it stay valid. The collection is prefixed with the
 * library name (same convention as imported variables) and is never allowed to
 * become the project's default collection.
 */
export const addElementPresetVariableCollection = (
  collection: StyleVariables.VariableCollection,
  libraryName: string,
  variableCollections: StyleVariables.VariableCollectionMap
): StyleVariables.VariableCollectionMap => {
  // Never overwrite an existing entry. A soft-deleted collection stays deleted:
  // overwriting it here would restore it locally without a matching server
  // update. `resolveElementPresetVariableCollection` reroutes variables away
  // from deleted collections before this is called.
  if (variableCollections.has(collection.id)) return variableCollections;

  return applyBrand(
    variableCollections.set(collection.id, {
      ...collection,
      modes: collection.modes ?? {},
      name: `${libraryName}/${collection.name}`,
      isDefault: false,
    })
  );
};

/**
 * Imported library variables carry the `collectionId` of the library's own
 * variable collection. That collection does not exist in the project until it
 * is imported too, and the multiplayer server rejects any variable whose
 * collection is missing ("The collection [...] does not exist"), which fails
 * the whole element insertion.
 *
 * Given a plugin variable, returns the variable to import together with the
 * project variable collections it needs:
 * - if the project already has the collection, nothing changes
 * - if the project has the collection but the user deleted it, the variable
 *   falls back to the project's default collection (it would otherwise be
 *   hidden inside the deleted collection)
 * - if the plugin ships the collection, it is imported alongside the variable
 * - otherwise the variable falls back to the project's default collection
 */
export const resolveElementPresetVariableCollection = (
  variable: StyleVariables.AnyVariableType,
  plugins: Plugins.PluginsType,
  pluginName: PluginName,
  libraryName: string,
  variableCollections: StyleVariables.VariableCollectionMap
): {
  variable: StyleVariables.AnyVariableType;
  variableCollections: StyleVariables.VariableCollectionMap;
} => {
  const {collectionId} = variable;
  if (!collectionId) return {variable, variableCollections};

  const projectCollection = variableCollections.get(collectionId);
  if (projectCollection && !projectCollection.deleted) {
    return {variable, variableCollections};
  }

  const pluginCollection = projectCollection
    ? undefined
    : Plugins.getPluginStyleVariableCollection(
        plugins,
        qualify(pluginName, collectionId)
      );

  if (pluginCollection && !pluginCollection.deleted) {
    return {
      variable,
      variableCollections: addElementPresetVariableCollection(
        pluginCollection,
        libraryName,
        variableCollections
      ),
    };
  }

  const variableInDefaultCollection: StyleVariables.AnyVariableType = {
    ...variable,
    collectionId: undefined,
  };
  return {variable: variableInDefaultCollection, variableCollections};
};

export const initialCssVariablesState = {
  swatches: [],
  variables: StyleVariables.emptyVariableMap,
  variableCollections: StyleVariables.emptyVariableCollectionMap,
  ephemeralVariables: null,
  sync: SYNC_STATUS.IN_SYNC,
  syncRetryCount: 0,
};

export const reducer = (
  state: State = initialCssVariablesState,
  action: Action
): State => {
  switch (action.type) {
    case UNDO: {
      return assoc('ephemeralVariables')(null)(state);
    }
    case REDO: {
      return assoc('ephemeralVariables')(null)(state);
    }

    case ELEMENT_ADDED: {
      const {elementPreset} = action.payload;
      if (!elementPreset) return state;

      const plugins = getPluginsFromState(action.state);
      const elementPresetType = Plugins.getElementPreset(
        plugins,
        elementPreset
      );
      if (!elementPresetType) return state;

      const pluginId = getPluginName(elementPreset);
      const libraries = action.state.DesignerStore.libraries;

      // if plugin doesn't belong to libraries we don't need to do anything else
      if (!(pluginId in libraries)) return state;

      let updatedVariables = state.variables;
      let updatedVariableCollections = state.variableCollections;
      let updatedState = state;

      // Part 1 - treating libraries that were created with swatches
      const library = libraries[pluginId];

      // To make TS happy
      if (!library) return state;

      const {displayName: libraryName} = library;
      const swatches = ElementPreset.getSwatches(elementPresetType) || [];

      // Note: We need to import swatches even when style variables are enabled as we are not migrating currently references to swatches in the StyleBlocks
      // so to render styles correctly on the canvas we need to import swatches as well
      const index = swatchIndex(state.swatches);

      const swatchesToAdd: Array<SwatchType> = [];
      swatches.forEach((swatch: SwatchType) => {
        if (!index.has(swatch.id)) {
          // prefix imported library variables names with library name to
          // group libraries variables separate from user variables
          const updatedSwatch = {
            ...swatch,
            name: `${libraryName}/${swatch.name}`,
          } as const;
          swatchesToAdd.push(updatedSwatch);
        }
      });

      if (swatchesToAdd.length) {
        const updatedSwatches: Array<SwatchType> =
          state.swatches.concat(swatchesToAdd);

        updatedState = assoc('swatches')(updatedSwatches)(updatedState);

        // running logic over swatchesToAdd allows us to rely on the naming convention of the swatches for ones that originated from libraries
        swatchesToAdd.forEach((swatch: SwatchType) => {
          const variable = StyleVariables.colorVariableFromSwatch(swatch);

          if (variable && !updatedVariables.has(variable.id)) {
            const variableWithoutRepeatedName =
              StyleVariables.createVariableWithoutRepeatedName(
                updatedVariables,
                variable
              );
            updatedVariables = StyleVariables.upsertVariable(
              updatedVariables,
              variableWithoutRepeatedName
            );
          }
        });
      }

      // Part 2 - treating libraries that were created with style variables

      // Part 2.1 - Treating libraries compiled with legacy compiler where all variables data is stored directly on presets
      const presetStyleVariables =
        ElementPreset.getStyleVariables(elementPresetType);

      const presetStyleVariableQualifiedIds =
        ElementPreset.getStyleVariableIds(elementPresetType);

      if (presetStyleVariables && presetStyleVariableQualifiedIds) {
        reportBug(
          'Corrupted library preset - style variables',
          'Library element preset contains both preset variables and preset variables ids. This is not expected.',
          {
            team: 'scale',
            priority: 'error',
            ticket: 'https://webflow.atlassian.net/browse/SCALE-78',
            pluginId,
          }
        );
        return state;
      }

      if (presetStyleVariables) {
        presetStyleVariables.forEach(
          (presetVariable: StyleVariables.AnyVariableType) => {
            if (presetVariable && !updatedVariables.has(presetVariable.id)) {
              const {variable, variableCollections} =
                resolveElementPresetVariableCollection(
                  presetVariable,
                  plugins,
                  pluginId,
                  libraryName,
                  updatedVariableCollections
                );
              updatedVariableCollections = variableCollections;
              updatedVariables = addElementPresetVariable(
                variable,
                libraryName,
                updatedVariables
              );
            }
          }
        );
      }

      // Part 2.2 - Treating libraries compiled with new compiler where variables data is stored at the plugin level
      // and each preset contains references to the variables
      if (presetStyleVariableQualifiedIds) {
        const elementPresetImportStrategy =
          Plugins.getElementPresetImportStrategy(plugins, elementPreset);

        const pluginName = getPluginName(elementPreset);

        const variablesOriginIdMap =
          elementPresetImportStrategy === Plugins.IMPORT_STRATEGY.VALUE
            ? getLibrariesVariablesOriginIdMap(updatedVariables)(pluginName)
            : {};

        const isVariablesAlreadyAdded = (
          pluginVariableQualifiedId: Qualified<string>
        ) =>
          elementPresetImportStrategy === Plugins.IMPORT_STRATEGY.VALUE
            ? variablesOriginIdMap[getName(pluginVariableQualifiedId)]
            : updatedVariables.has(getName(pluginVariableQualifiedId));

        presetStyleVariableQualifiedIds.forEach((pluginVariableQualifiedId) => {
          if (!isVariablesAlreadyAdded(pluginVariableQualifiedId)) {
            const pluginVariable = Plugins.getPluginStyleVariable(
              plugins,
              pluginVariableQualifiedId
            );

            if (pluginVariable) {
              const {variable, variableCollections} =
                resolveElementPresetVariableCollection(
                  pluginVariable,
                  plugins,
                  pluginName,
                  libraryName,
                  updatedVariableCollections
                );
              updatedVariableCollections = variableCollections;
              updatedVariables = addElementPresetVariable(
                variable,
                libraryName,
                updatedVariables
              );
            }
          }
        });
      }

      updatedState = assoc('variables')(updatedVariables)(updatedState);
      if (updatedVariableCollections !== state.variableCollections) {
        updatedState = assoc('variableCollections')(updatedVariableCollections)(
          updatedState
        );
      }
      return updatedState;
    }

    case SWATCH_UPDATE_BEGAN: {
      return {
        swatches: state.swatches,
        variables: state.variables,
        variableCollections: state.variableCollections,
        ephemeralVariables: state.ephemeralVariables,
        sync: state.sync,
        syncRetryCount: state.syncRetryCount,
      };
    }
    case SWATCH_ADDED: {
      return assoc('swatches')(action.swatches)(state);
    }

    case SWATCH_UPDATED: {
      return assoc('swatches')(action.swatches)(state);
    }

    case SWATCH_REMOVED: {
      return assoc('swatches')(action.swatches)(state);
    }

    case SWATCH_EDITING_CANCELLED: {
      return assoc('swatches')(action.swatches)(state);
    }

    case SWATCH_EDITED: {
      return assoc('swatches')(action.swatches)(state);
    }

    case SWATCHES_INJECTED: {
      return action.payload && action.payload.length === 0
        ? assoc('swatches')(emptyArray)(state)
        : assoc('swatches')(cleanSwatches(action.payload))(state);
    }

    case STYLE_VARIABLES_LOADED: {
      const result = StyleVariables.processServerStyleVariablesData(
        state,
        action.payload
      );
      return assoc('syncRetryCount')(0)(result);
    }

    case STYLE_VARIABLES_EPHEMERALLY_UPDATED: {
      const {variables} = action.payload;

      const variableValidationError = StyleVariables.validateVariablesUpdate(
        state.variables,
        variables
      );
      if (variableValidationError) {
        return state;
      }

      state = assoc('ephemeralVariables')(variables)(state);
      return state;
    }

    case STYLE_VARIABLE_EPHEMERAL_UPDATE_CANCELLED: {
      return assoc('ephemeralVariable')(null)(state);
    }

    case STYLE_VARIABLE_UPDATED: {
      if (!state.variables) return state;

      const {variable} = action.payload;
      const variableValidationError = StyleVariables.validateVariableUpdate(
        state.variables,
        variable
      );
      if (variableValidationError) {
        reportBug(
          'StyleVariableUpdatedValidationError',
          'Encountered a `variableValidationError` when handling the `STYLE_VARIABLE_UPDATED` action',
          {
            team: 'dse',
            priority: 'warning',
            ticket: 'https://webflow.atlassian.net/browse/DSE-7313',
            validationError: variableValidationError.type,
            variable,
          }
        );
        return state;
      }

      const prevVariable = state.variables.get(variable.id);
      const isRestoreUpdate = prevVariable?.deleted && !variable.deleted;
      const {collectionId} = variable;

      // 1. If variable has a collectionId...
      if (isRestoreUpdate && collectionId) {
        const collection = state.variableCollections.get(collectionId);

        // 2. And the collection is deleted....
        if (collection && collection.deleted) {
          // 3. Then we also need to restore the collection
          state = {
            ...state,
            variableCollections: applyBrand(
              state.variableCollections.set(collectionId, {
                ...collection,
                deleted: false,
              })
            ),
          };
        }
      }

      const variables = StyleVariables.upsertVariable(
        state.variables,
        variable
      );

      state = assoc('variables')(variables)(state);
      state = assoc('ephemeralVariables')(null)(state);

      return state;
    }

    case STYLE_VARIABLES_BULK_UPDATED: {
      if (!state.variables) return state;

      const updatedVariableMap = handleStyleVariablesBulkUpdatedAction(
        state.variables,
        action
      );

      state = assoc('variables')(updatedVariableMap)(state);
      state = assoc('ephemeralVariables')(null)(state);

      return state;
    }

    case STYLE_VARIABLES_GROUP_UPDATED: {
      if (!state.variables) return state;

      const {variableIds, groupName} = action.payload;

      const updatedVariables = variableIds.reduce<
        StyleVariables.AnyVariableType[]
      >((res, variableId) => {
        const variable = state.variables.get(variableId);

        if (!variable) return res;

        const currName = StyleVariables.getVariableDisplayName(variable);
        const updatedName = groupName ? `${groupName}/${currName}` : currName;

        res.push({
          ...variable,
          name: updatedName,
        });

        return res;
      }, []);

      const updatedVariableMap = StyleVariables.upsertMultipleVariables(
        state.variables,
        updatedVariables
      );

      return assoc('variables')(updatedVariableMap)(state);
    }

    case STYLE_VARIABLES_GROUP_DUPLICATED: {
      if (!state.variables) return state;

      const {variables: variablesToDuplicate} = action.payload;
      if (variablesToDuplicate.length === 0) return state;

      const lastOrder = (
        state.variables.findLast((v) =>
          StyleVariables.isValidOrderValue(v.order)
        ) ?? null
      )?.order;

      const newOrderValues = StyleVariables.generateNKeysBetween(
        lastOrder,
        null,
        variablesToDuplicate.length
      );

      if (newOrderValues instanceof Error) {
        reportBug('UnableToGenerateVariableOrderKeys', newOrderValues.message, {
          team: 'styles',
          ticket: 'https://webflow.atlassian.net/browse/STYL-579',
        });
        return state;
      }

      let updatedVariables = state.variables;
      for (let i = 0; i < variablesToDuplicate.length; i++) {
        const variable = variablesToDuplicate[i];
        const order = newOrderValues[i];
        if (variable == null || order == null) continue;

        const withUniqueName = StyleVariables.createVariableWithoutRepeatedName(
          updatedVariables,
          variable
        );
        updatedVariables = updatedVariables.upsert({
          ...withUniqueName,
          order,
        });
      }

      return assoc('variables')(updatedVariables)(state);
    }

    case STYLE_VARIABLE_CREATED: {
      if (!state.variables) return state;

      const variable = StyleVariables.createVariableWithoutRepeatedName(
        state.variables,
        action.payload.variable
      );

      const canAccessVariablesPanel = selectDesignerAbility(
        'canAccessVariablesPanel'
      )(action.state);

      if (!canAccessVariablesPanel) {
        const variables = state.variables.upsert(variable);
        return assoc('variables')(variables)(state);
      }

      const order = StyleVariables.generateNewVariableOrder(
        state.variables,
        variable
      );

      const {collectionId} = action.payload.variable;
      if (action.payload.newCollectionName && collectionId) {
        if (order instanceof Error) {
          reportBug(
            'UnableToGenerateVariableCollectionOrderKey',
            order.message,
            {
              team: 'styles',
              ticket: 'https://webflow.atlassian.net/browse/STYL-579',
            }
          );
          return state;
        }

        state = assoc('variableCollections')(
          applyBrand(
            state.variableCollections.set(collectionId, {
              id: collectionId,
              name: action.payload.newCollectionName,
              modes: {},
              order,
              version: 1,
            })
          )
        )(state);
      }

      if (order instanceof Error) {
        reportBug('UnableToGenerateNewVariableOrderKey', order.message, {
          team: 'styles',
          ticket: 'https://webflow.atlassian.net/browse/STYL-579',
        });
        return state;
      }

      const variables = state.variables.upsert({...variable, order});

      return assoc('variables')(variables)(state);
    }
    case STYLE_VARIABLE_DELETED: {
      if (!state.variables) return state;

      const variables = StyleVariables.deleteVariable(
        state.variables,
        action.payload.variableId
      );

      return assoc('variables')(variables)(state);
    }

    case STYLE_VARIABLE_MODE_DELETED_FAILED:
    case STYLE_VARIABLE_COLLECTION_CREATED_FAILED:
    case STYLE_VARIABLE_COLLECTION_UPDATED_FAILED:
    case STYLE_VARIABLE_COLLECTION_DELETED_FAILED:
    case STYLE_VARIABLE_CREATED_FAILED:
    case STYLE_VARIABLE_UPDATED_FAILED:
    case STYLE_VARIABLE_DELETED_FAILED:
    case STYLE_VARIABLE_BULK_UPDATE_FAILED: {
      return assoc('sync')(SYNC_STATUS.OUT_OF_SYNC)(state);
    }
    case STYLE_VARIABLES_BULK_SAVE_ALL_FAILED: {
      const newRetryCount = (state.syncRetryCount ?? 0) + 1;
      return assoc('syncRetryCount')(newRetryCount)(
        assoc('sync')(SYNC_STATUS.OUT_OF_SYNC)(state)
      );
    }

    case STYLE_VARIABLES_BULK_SAVE_ALL_SUCCESS: {
      return assoc('syncRetryCount')(0)(
        assoc('sync')(SYNC_STATUS.IN_SYNC)(state)
      );
    }

    case STYLE_VARIABLES_BULK_SAVE_ALL: {
      return assoc('sync')(SYNC_STATUS.SYNCING)(state);
    }

    case STYLE_VARIABLES_REORDERED: {
      const newVariables = handleStyleVariablesReorderedAction(
        state.variables,
        state.variableCollections,
        Plugins.getPluginsSlugMap(action.state.DesignerStore.plugins),
        action
      );
      if (newVariables instanceof Error) {
        return state;
      }

      return {
        ...state,
        variables: newVariables,
      };
    }

    case STYLE_VARIABLE_GROUP_REORDERED: {
      const result = handleStyleVariableGroupReorderedAction(
        state.variables,
        state.variableCollections,
        action
      );
      if (result instanceof Error) {
        return state;
      }

      return {
        ...state,
        variables: result.variables,
      };
    }

    // Variable Collections ----------------------------------------------
    case STYLE_VARIABLE_COLLECTION_CREATED: {
      const {id, name, duplicateSourceId} = action.payload;

      const newOrder = StyleVariables.generateKeyBetween(
        state.variableCollections.last()?.order ?? null,
        null
      );

      if (newOrder instanceof Error) {
        reportBug(
          'UnableToGenerateVariableCollectionOrderKey',
          newOrder.message,
          {
            team: 'styles',
            ticket: 'https://webflow.atlassian.net/browse/STYL-579',
          }
        );
        return state;
      }

      // Handle creating a brand new collection
      if (duplicateSourceId == null) {
        return assoc('variableCollections')(
          applyBrand(
            state.variableCollections.set(id, {
              id,
              name,
              modes: {},
              order: newOrder,
              version: 1,
            })
          )
        )(state);
      }

      // Handle duplicating an existing collection
      const sourceCollection = state.variableCollections.get(duplicateSourceId);
      if (!sourceCollection) return state;

      // Create a map of old mode IDs to new mode IDs
      const modeIdMap = Object.keys(sourceCollection.modes).reduce(
        (acc, oldModeId) => {
          const newModeId = StyleVariables.generateModeId();
          acc[oldModeId] = newModeId;
          return acc;
        },
        {} as Record<
          StyleVariables.VariableModeId,
          StyleVariables.VariableModeId
        >
      );

      const nextVariableCollections: StyleVariables.VariableCollectionMap =
        applyBrand(
          state.variableCollections.set(id, {
            ...sourceCollection,
            id,
            name,
            order: newOrder,
            isDefault: false,
            modes: Object.entries(sourceCollection.modes).reduce(
              (acc, [oldModeId, mode]) => {
                const newModeId = modeIdMap[oldModeId];
                if (!mode.deleted && newModeId) {
                  acc[newModeId] = {...mode, id: newModeId};
                }
                return acc;
              },
              {} as StyleVariables.VariableModesMap
            ),
          })
        );

      // To duplicate variables in a variable collection, we do it in two passes.
      //
      // Pass 1:
      //   • Narrow down the list of variables to duplicate
      //   • Generate new variable IDs, and create a map of old variable IDs to new variable IDs.
      //   • Generate new variable names
      //   • Set the new collectionId
      const {variableIdMap, variablesToDuplicate} = state.variables.reduce(
        (res, variable) => {
          if (variable.deleted) return res;

          const variableCollection = StyleVariables.getVariableCollection(
            variable,
            state.variableCollections
          );

          if (variableCollection?.id !== duplicateSourceId) return res;

          const newVariable = {
            ...variable,
            id: StyleVariables.generateVariableId(),
            collectionId: id,
            name: incrementString(
              res.existingVariableNames,
              variable.name,
              '-',
              StyleVariables.sanitizeForVariableName
            ),
          };

          res.variableIdMap[variable.id] = newVariable.id;
          res.existingVariableNames.push(newVariable.name);
          res.variablesToDuplicate.push(newVariable);

          return res;
        },
        {
          variableIdMap: {} as Record<
            StyleVariables.VariableID,
            StyleVariables.VariableID
          >,
          variablesToDuplicate: [] as StyleVariables.AnyVariableType[],
          existingVariableNames: state.variables.reduce((acc, v) => {
            if (!v.deleted && v.origin == null) acc.push(v.name);
            return acc;
          }, [] as string[]),
        }
      );

      // Now, we generate the new order values for our duplicated variables.
      // newOrderValues.length should match the length of the duplicated variables.
      const lastOrder = (
        state.variables.findLast((v) =>
          StyleVariables.isValidOrderValue(v.order)
        ) ?? null
      )?.order;

      const newOrderValues = StyleVariables.generateNKeysBetween(
        lastOrder,
        null,
        variablesToDuplicate.length
      );

      if (newOrderValues instanceof Error) {
        reportBug('UnableToGenerateVariableOrderKeys', newOrderValues.message, {
          team: 'styles',
          ticket: 'https://webflow.atlassian.net/browse/STYL-579',
        });
        return state;
      }

      // Pass 2:
      //   • Update duplicated variable references, so if a duplicated variable
      //     references another duplicated variable in the collection,
      //     we update all references to the new variable ID.
      const nextVariables = state.variables.upsertAll(
        variablesToDuplicate.map((variable, i) => {
          const nextModes = variable.modes
            ? Object.entries(variable.modes).reduce<
                NonNullable<StyleVariables.AnyVariableType['modes']>
              >((acc, [oldModeId, modeValue]) => {
                if (modeIdMap[oldModeId]) {
                  const newVariableIdRef = isVariableReference(modeValue)
                    ? variableIdMap[modeValue.value.variableId]
                    : null;

                  acc[modeIdMap[oldModeId]] = newVariableIdRef
                    ? {
                        type: 'ref',
                        value: {
                          variableId: newVariableIdRef,
                        },
                      }
                    : modeValue;
                }

                return acc;
              }, {})
            : {};

          const newVariableIdRef = isVariableReference(variable.value)
            ? variableIdMap[variable.value.value.variableId]
            : null;

          const nextValue = newVariableIdRef
            ? {
                type: 'ref',
                value: {
                  variableId: newVariableIdRef,
                },
              }
            : variable.value;

          return {
            ...variable,
            order: newOrderValues[i],
            value: nextValue,
            modes: nextModes,
          } as StyleVariables.AnyVariableType;
        })
      );

      return {
        ...state,
        variables: nextVariables,
        variableCollections: nextVariableCollections,
      };
    }

    case STYLE_VARIABLE_COLLECTION_UPDATED: {
      const {id, name} = action.payload;

      const variableCollection = state.variableCollections.get(id);

      if (variableCollection) {
        return assoc('variableCollections')(
          state.variableCollections.set(id, {...variableCollection, name})
        )(state);
      }

      return state;
    }

    case STYLE_VARIABLE_COLLECTION_DELETED: {
      const {id} = action.payload;
      const collection = state.variableCollections.get(id);

      if (collection) {
        // We don't allow deleting the default collection.
        if (collection.isDefault) return state;

        // Soft delete variable collection
        const nextVariableCollections: StyleVariables.VariableCollectionMap =
          applyBrand(
            state.variableCollections.set(id, {...collection, deleted: true})
          );

        // Soft delete variables from collection
        const nextVariables = state.variables.upsertAll(
          state.variables.reduce<StyleVariables.AnyVariableType[]>((res, v) => {
            if (v.collectionId === id) {
              res.push({...v, deleted: true});
            }
            return res;
          }, [])
        );

        state = assoc('variables')(nextVariables)(state);
        return assoc('variableCollections')(nextVariableCollections)(state);
      }

      return state;
    }

    case STYLE_VARIABLE_COLLECTION_REORDERED: {
      const {sourceCollectionId, targetCollectionId, position} = action.payload;
      const {variableCollections} = state;

      const sourceCollection = variableCollections.get(sourceCollectionId);
      const targetCollection = variableCollections.get(targetCollectionId);

      if (!sourceCollection || !targetCollection) return state;

      const collectionsArray = variableCollections.toArray();
      const sourceIndex = collectionsArray.findIndex(
        (c) => c.id === sourceCollectionId
      );
      const targetIndex = collectionsArray.findIndex(
        (c) => c.id === targetCollectionId
      );

      if (
        sourceIndex === -1 ||
        targetIndex === -1 ||
        targetIndex === sourceIndex
      ) {
        return state;
      }

      let beforeOrder: string | null = null;
      let afterOrder: string | null = null;

      if (position === 'after') {
        // Moving forward
        beforeOrder = targetCollection.order;
        afterOrder = collectionsArray[targetIndex + 1]?.order ?? null;
      } else {
        // Moving backward
        beforeOrder = collectionsArray[targetIndex - 1]?.order ?? null;
        afterOrder = targetCollection.order;
      }

      const newOrder = StyleVariables.generateKeyBetween(
        beforeOrder,
        afterOrder
      );

      if (newOrder instanceof Error) {
        reportBug(
          'UnableToGenerateVariableCollectionOrderKey',
          newOrder.message,
          {
            team: 'styles',
            ticket: 'https://webflow.atlassian.net/browse/STYL-579',
          }
        );
        return state;
      }

      const updatedSourceCollection = {
        ...sourceCollection,
        order: newOrder,
      };

      const newVariableCollections = variableCollections
        .remove(sourceCollectionId)
        .set(sourceCollectionId, updatedSourceCollection);

      return assoc('variableCollections')(
        StyleVariables.sortByVariableCollectionOrder(
          applyBrand(newVariableCollections)
        )
      )(state);
    }

    case STYLE_VARIABLES_CROSS_COLLECTION_MOVED: {
      const {modesToCopy, variableIds, targetCollectionId, newCollectionName} =
        action.payload;

      let {variables, variableCollections} = state;

      if (newCollectionName) {
        const newOrder = StyleVariables.generateKeyBetween(
          state.variableCollections.last()?.order ?? null,
          null
        );

        if (newOrder instanceof Error) {
          reportBug(
            'UnableToGenerateVariableCollectionOrderKey',
            newOrder.message,
            {
              team: 'styles',
              ticket: 'https://webflow.atlassian.net/browse/STYL-579',
            }
          );
          return state;
        }

        variableCollections = applyBrand(
          variableCollections.set(targetCollectionId, {
            id: targetCollectionId,
            name: newCollectionName,
            modes: {},
            order: newOrder,
            version: 1,
          })
        );
      }

      const variableIdsSet = new Set(variableIds);

      if (modesToCopy.length === 0) {
        for (const variable of variables) {
          if (variableIdsSet.has(variable.id)) {
            variables = variables.upsert({
              ...variable,
              collectionId: targetCollectionId,
              modes: {},
            });
          }
        }

        return {
          ...state,
          variables,
          variableCollections,
        };
      }

      const modeIdMap: Record<
        StyleVariables.VariableModeId,
        StyleVariables.VariableModeId
      > = {};

      for (const variableId of variableIds) {
        const variable = variables.get(variableId);
        if (!variable) continue;

        const updatedModes: NonNullable<
          StyleVariables.AnyVariableType['modes']
        > = {};

        // Process each mode override in the variable
        for (const sourceModeId of modesToCopy) {
          const targetCollection = variableCollections.get(targetCollectionId);
          if (!targetCollection) continue;

          const sourceCollection = StyleVariables.getVariableCollection(
            variable,
            variableCollections
          );
          if (!sourceCollection) continue;

          const sourceMode = sourceCollection.modes[sourceModeId];
          if (!sourceMode) continue;

          const existingModeNames = Object.values(
            targetCollection.modes
          ).reduce<Array<string>>((acc, m) => {
            if (!m.deleted) acc.push(m.name);
            return acc;
          }, []);

          let newModeId = modeIdMap[sourceModeId];

          // if the mode doesn't exist in the target collection, create it
          if (newModeId == null) {
            const newName = increment(existingModeNames, sourceMode.name, ' ');
            newModeId = StyleVariables.generateModeId();

            const newMode: StyleVariables.VariableMode = {
              id: newModeId,
              name: newName,
              deleted: false,
            };

            const newModes: StyleVariables.VariableModesMap = {
              ...targetCollection.modes,
              [newModeId]: newMode,
            };

            const newCollection: StyleVariables.VariableCollection = {
              ...targetCollection,
              modes: newModes,
            };

            variableCollections = applyBrand(
              variableCollections.set(targetCollection.id, newCollection)
            );

            modeIdMap[sourceModeId] = newModeId;
            existingModeNames.push(newName);
          }

          const modeValue = variable.modes[sourceModeId];
          if (modeValue) {
            // Use the new mode ID for the variable's mode override
            updatedModes[newModeId] = modeValue;
          }
        }

        // Update the variable with new collection ID and updated mode
        variables = variables.upsert({
          ...variable,
          collectionId: targetCollectionId,
          modes: updatedModes,
        } as StyleVariables.AnyVariableType);
      }

      return {
        ...state,
        variables,
        variableCollections,
      };
    }

    // Variable Modes ----------------------------------------------------
    case STYLE_VARIABLE_MODE_CREATED: {
      const {payload} = action;
      const {id, collectionId, name, duplicateSourceId, shouldDuplicateBase} =
        payload;

      const collection = state.variableCollections.get(collectionId);
      if (collection == null) return state;

      if (duplicateSourceId) {
        const {modes} = collection;

        const newMode: StyleVariables.VariableMode = {
          id,
          name,
          deleted: false,
        };
        const newModes: StyleVariables.VariableModesMap = {
          ...modes,
          [id]: newMode,
        };
        const newCollection: StyleVariables.VariableCollection = {
          ...collection,
          modes: newModes,
        };

        state = assoc('variables')(
          state.variables.upsertAll(
            state.variables.reduce<StyleVariables.AnyVariableType[]>(
              (res, v) => {
                const variableCollection = StyleVariables.getVariableCollection(
                  v,
                  state.variableCollections
                );

                if (variableCollection?.id === collectionId && v.modes) {
                  const modeValue = shouldDuplicateBase
                    ? v.value
                    : v.modes[duplicateSourceId];

                  if (modeValue) {
                    res.push({
                      ...v,
                      modes: {...v.modes, [id]: modeValue},
                    } as StyleVariables.AnyVariableType);
                  }
                }
                return res;
              },
              []
            )
          )
        )(state);

        return assoc('variableCollections')(
          applyBrand(state.variableCollections.set(collectionId, newCollection))
        )(state);
      }

      const newMode = {id, name};
      const newModes = {...collection.modes, [id]: newMode};
      const newCollection = {...collection, modes: newModes};

      return assoc('variableCollections')(
        applyBrand(state.variableCollections.set(collectionId, newCollection))
      )(state);
    }

    case STYLE_VARIABLE_MODE_CONFIG_UPDATED: {
      const {payload} = action;
      const {id, collectionId} = payload;

      const collection = state.variableCollections.get(collectionId);
      if (collection == null) return state;

      const {modes} = collection;
      let mode = modes[id];

      // The base mode has no stored entry on a collection until the first
      // rename. To allow the rename to be persisted in `collection.modes`, we
      // synthesize an entry the first time we see a non-empty `name` for `id
      // === 'base'`, so we only abort a rename operation for `null`ish modes
      // when the mode is not the base mode:
      if (
        mode == null &&
        id === StyleVariables.BASE_VARIABLE_MODE_ID &&
        payload.name
      ) {
        mode = {
          id,
          name: StyleVariables.BASE_VARIABLE_MODE_DEFAULT_NAME,
        };
      }

      if (mode == null) return state;

      const updatedMode: StyleVariables.VariableMode = {
        ...mode,
        ...(payload.name && {name: payload.name}),
        ...(payload.breakpointId && {breakpointId: payload.breakpointId}),
      };

      if (updatedMode !== mode) {
        const newModes = {...modes, [id]: updatedMode};
        const newCollection = {...collection, modes: newModes};

        return assoc('variableCollections')(
          applyBrand(state.variableCollections.set(collectionId, newCollection))
        )(state);
      }

      return state;
    }

    case STYLE_VARIABLE_MODE_VALUE_UPDATED: {
      return handleStyleVariableModeValueUpdatedAction(state, action).state;
    }

    // Reorder modes by rewriting the `modes` Record's key order
    // (STYL-2355). The base mode is implicit (not present in `modes`) and
    // is always pinned as the first column, so it is excluded from this
    // operation.
    case STYLE_VARIABLE_MODE_REORDERED: {
      const {collectionId, sourceModeId, targetModeId, position} =
        action.payload;

      if (
        sourceModeId === StyleVariables.BASE_VARIABLE_MODE_ID ||
        targetModeId === StyleVariables.BASE_VARIABLE_MODE_ID ||
        sourceModeId === targetModeId
      ) {
        return state;
      }

      const collection = state.variableCollections.get(collectionId);
      if (collection == null) return state;

      const {modes} = collection;
      if (modes[sourceModeId] == null || modes[targetModeId] == null) {
        return state;
      }

      const orderedKeys = Object.keys(modes).filter(
        (key) => key !== sourceModeId
      );
      const targetIndex = orderedKeys.indexOf(targetModeId);
      if (targetIndex === -1) return state;
      const insertAt = position === 'after' ? targetIndex + 1 : targetIndex;
      orderedKeys.splice(insertAt, 0, sourceModeId);

      const newModes: StyleVariables.VariableModesMap = {};
      for (const key of orderedKeys) {
        const mode = modes[key];
        if (mode != null) {
          newModes[key] = mode;
        }
      }

      // Bail if order is unchanged — avoids a redundant PATCH and re-render.
      const prevKeys = Object.keys(modes);
      const isUnchanged =
        prevKeys.length === orderedKeys.length &&
        prevKeys.every((key, i) => key === orderedKeys[i]);
      if (isUnchanged) return state;

      const newCollection: StyleVariables.VariableCollection = {
        ...collection,
        modes: newModes,
      };

      return assoc('variableCollections')(
        applyBrand(state.variableCollections.set(collectionId, newCollection))
      )(state);
    }

    // The behavior of these actions are identical, except we change `deleted`
    // to either true or false depending on the action type.
    case STYLE_VARIABLE_MODE_RESTORED:
    case STYLE_VARIABLE_MODE_DELETED: {
      const {id: modeId, collectionId} = action.payload;
      const restoring = action.type === STYLE_VARIABLE_MODE_RESTORED;

      const variableCollection = state.variableCollections.get(collectionId);
      if (!variableCollection) return state;

      const nextVariableCollection: StyleVariables.VariableCollection = {
        ...variableCollection,
        // If we're restoring the mode, we want to also restore its collection
        // (if needed), otherwise we shouldn't change
        // `variableCollection.deleted` at all:
        deleted:
          restoring && variableCollection.deleted
            ? false
            : variableCollection.deleted,
        modes: Object.fromEntries(
          Object.entries(variableCollection.modes).map(([key, mode]) => [
            key,
            key === modeId ? {...mode, deleted: !restoring} : mode,
          ])
        ),
      };

      const updatedCollections: StyleVariables.VariableCollectionMap =
        applyBrand(
          state.variableCollections.set(collectionId, nextVariableCollection)
        );

      return assoc('variableCollections')(updatedCollections)(state);
    }

    case STYLE_VARIABLES_ORION_APP_VARIABLES_CREATED: {
      const {
        payload: {
          variables: orionVariables,
          variableCollection: orionVariableCollection,
        },
      } = action;

      const updatedVariables = state.variables.upsertAll(orionVariables);

      const updatedVariableCollections = applyBrand(
        state.variableCollections.set(
          orionVariableCollection.id,
          orionVariableCollection
        )
      );

      return assoc('variables')(updatedVariables)(
        assoc('variableCollections')(updatedVariableCollections)(state)
      );
    }

    default: {
      const _exhaustiveCheck: never = action;
      return state;
    }
  }
};

/**
 * Shared logic for updating a variable's mode value.
 *
 * Returns an updated CssVariablesStore state, as well as the ID of the variable
 * that was updated, or `null` if nothing was updated.
 *
 * Use this data to ensure the correct variables data is persisted in
 * `RequestStore`.
 *
 * Learn more about variable persistence tech debt here:
 * https://webflow.atlassian.net/wiki/spaces/DB/pages/766345338/Improve+variables+persistence+code
 */
export const handleStyleVariableModeValueUpdatedAction = (
  state: State,
  action: DesignerActionFromCreator<typeof styleVariableModeValueUpdated>
): {
  /** The updated CssVariablesStore state */
  state: State;
  /** The ID of the variable that was updated. `null` if no variable was updated for any reason. */
  updatedVariableId: null | StyleVariables.VariableID;
} => {
  const {modeId, variableId, value} = action.payload;

  const variable = state.variables.get(variableId);
  if (!variable) return {state, updatedVariableId: null};

  const variableCollection = StyleVariables.getVariableCollection(
    variable,
    state.variableCollections
  );
  // If the variable's collection cannot be determined, or it has no mode
  // matching `modeId`, we quietly abort the action handler:
  if (variableCollection == null || variableCollection.modes[modeId] == null)
    return {state, updatedVariableId: null};

  const variableModeValidationError =
    StyleVariables.validateVariableModeValueUpdate(
      state.variables,
      variable,
      value,
      modeId
    );

  if (variableModeValidationError) {
    reportBug(
      'StyleVariableModeValueUpdatedValidationError',
      'Encountered a `variableModeValidationError` when handling the `STYLE_VARIABLE_MODE_VALUE_UPDATED` action',
      {
        team: 'styles',
        priority: 'warning',
        ticket: 'https://webflow.atlassian.net/browse/STYL-100',
        validationError: variableModeValidationError.type,
        variable,
      }
    );
    return {state, updatedVariableId: null};
  }

  if (!variable.modes) return {state, updatedVariableId: null};

  const nextModes =
    // If `null`, clear the value override so the mode will derive its value from the base/default mode.
    value == null
      ? omit([modeId])(variable.modes)
      : {
          ...variable.modes,
          [modeId]: value,
        };

  const nextVariables = state.variables.upsert({
    ...variable,
    modes: nextModes,
  });

  state = assoc('variables')(nextVariables)(state);

  // We must also clear any ephemeral changes that might have been in progress
  // as we consider this action the intention to "commit" these changes:
  state = assoc('ephemeralVariables')(null)(state);

  return {
    state,
    updatedVariableId: variableId,
  };
};

function CssVariablesStore(state: State, action: Action) {
  return reducer(state, action);
}

export {CssVariablesStore as default};
